import React, { useEffect, useMemo, useState } from "react";
import usePagination, { paginationRangeLabel, paginationSx } from "../../../hooks/usePagination";
import { dedupeSubjectsById, getSubjectTeacherLabel } from "../../../utils/dedupeSubjects";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { getClassDetails, getSubjectList } from "../../../redux/sclassRelated/sclassHandle";
import { deleteUser } from "../../../redux/userRelated/userHandle";
import {
    Alert,
    alpha,
    Box,
    Button,
    Chip,
    Container,
    Fade,
    IconButton,
    Menu,
    MenuItem,
    Paper,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    Pagination,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";
import PostAddIcon from "@mui/icons-material/PostAdd";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ClassOutlinedIcon from "@mui/icons-material/ClassOutlined";
import Popup from "../../../components/Popup";
import ConfirmDeleteDialog from "../../../components/ConfirmDeleteDialog";

const headCells = [
    { id: "subName", label: "Subject", width: "22%" },
    { id: "teacher", label: "Teacher", width: "18%" },
    { id: "type", label: "Type", width: "12%" },
    { id: "mie", label: "MIE code", width: "16%" },
    { id: "periods", label: "Periods / week", width: "12%" },
    { id: "actions", label: "Actions", align: "right", width: "20%" },
];

function TableSkeleton() {
    return (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        {headCells.map((c) => (
                            <TableCell key={c.id} sx={{ fontWeight: 600 }}>
                                {c.label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {[1, 2, 3, 4, 5].map((i) => (
                        <TableRow key={i}>
                            <TableCell colSpan={6}>
                                <Skeleton variant="rounded" height={44} sx={{ borderRadius: 1 }} />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function RowActions({ row, classId, onDeleteSubject }) {
    const navigate = useNavigate();
    const [menuAnchor, setMenuAnchor] = useState(null);

    return (
        <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
            <Tooltip title="Open subject" arrow>
                <IconButton
                    size="small"
                    color="primary"
                    onClick={() => navigate(`/Admin/subjects/subject/${classId}/${row.id}`)}
                    sx={{
                        bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                        "&:hover": { bgcolor: (t) => alpha(t.palette.primary.main, 0.16) },
                    }}
                >
                    <VisibilityOutlinedIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title="More" arrow>
                <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
                    <MoreHorizIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
                <MenuItem
                    onClick={() => {
                        setMenuAnchor(null);
                        onDeleteSubject(row);
                    }}
                    sx={{ color: "error.main", gap: 1 }}
                >
                    <DeleteOutlineIcon fontSize="small" />
                    Delete subject
                </MenuItem>
            </Menu>
        </Stack>
    );
}

const ShowSubjects = () => {
    const { classID } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { subjectsList, sclassDetails, loading, error, response } = useSelector((state) => state.sclass);

    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");

    useEffect(() => {
        if (!classID) {
            navigate("/Admin/subjects/chooseclass", { replace: true });
            return;
        }
        dispatch(getClassDetails(classID, "Sclass"));
        dispatch(getSubjectList(classID, "ClassSubjects"));
    }, [classID, dispatch, navigate]);

    const className = sclassDetails?.sclassName || "Class";

    const rows = useMemo(() => {
        if (!Array.isArray(subjectsList) || subjectsList.length === 0) return [];
        return dedupeSubjectsById(subjectsList).map((subject) => ({
            id: subject._id || subject.id,
            subName: subject.subName,
            teacherLabel: getSubjectTeacherLabel(subject),
            sessions: subject.sessions,
            mieCode: subject.mieSyllabusCode?.trim() || "—",
            isElective: Boolean(subject.isElective),
        }));
    }, [subjectsList]);

    const pageSize = 30;
    const { page, setPage, pageCount, paginatedItems, totalItems } = usePagination(rows, pageSize);

    const [deleteDialog, setDeleteDialog] = useState(null);

    const refresh = () => {
        if (classID) {
            dispatch(getClassDetails(classID, "Sclass"));
            dispatch(getSubjectList(classID, "ClassSubjects"));
        }
    };

    const closeDeleteDialog = () => setDeleteDialog(null);

    const openDeleteSubjectDialog = (row) => {
        setDeleteDialog({
            title: `Delete subject: ${row.subName}`,
            description:
                "This will permanently delete this subject and remove attendance and marks tied to it.",
            confirmWord: row.subName,
            onConfirm: async () => {
                const outcome = await dispatch(deleteUser(row.id, "Subject"));
                if (outcome?.success) {
                    refresh();
                    return;
                }
                setMessage("Could not delete the subject.");
                setShowPopup(true);
            },
        });
    };

    const openDeleteAllSubjectsDialog = () => {
        setDeleteDialog({
            title: `Delete all subjects in ${className}`,
            description: `This will permanently delete every subject for ${className}. This cannot be undone.`,
            confirmWord: "DELETE ALL SUBJECTS",
            onConfirm: async () => {
                const outcome = await dispatch(deleteUser(classID, "SubjectsClass"));
                if (outcome?.success) {
                    refresh();
                    return;
                }
                const detail = outcome?.error;
                setMessage(
                    detail ? `Could not delete all subjects: ${detail}` : "Could not delete all subjects.",
                );
                setShowPopup(true);
            },
        });
    };

    const handleConfirmDelete = async () => {
        if (deleteDialog?.onConfirm) {
            await deleteDialog.onConfirm();
        }
        closeDeleteDialog();
    };

    const hasRows = rows.length > 0;
    const showEmpty = !loading && !error && !hasRows;
    const showTable = !loading && !error && hasRows;

    return (
        <Box
            component="main"
            sx={{
                minHeight: "100%",
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.04)} 0%, ${t.palette.grey[50]} 28%)`
                        : alpha(t.palette.common.black, 0.2),
                pb: 6,
            }}
        >
            <Container maxWidth="lg" sx={{ pt: { xs: 2, sm: 3 }, pb: 2 }}>
                <Fade in timeout={380}>
                    <Stack spacing={3}>
                        <Button
                            startIcon={<ArrowBackIosNewOutlinedIcon sx={{ fontSize: 14 }} />}
                            onClick={() => navigate("/Admin/subjects/chooseclass")}
                            sx={{ alignSelf: "flex-start", fontWeight: 600 }}
                        >
                            Back to class list
                        </Button>

                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 2.5, sm: 3 },
                                borderRadius: 3,
                                color: "common.white",
                                background: (t) =>
                                    `linear-gradient(120deg, ${t.palette.secondary.dark} 0%, ${t.palette.secondary.main} 55%, ${alpha(
                                        t.palette.secondary.light,
                                        0.92
                                    )} 100%)`,
                                boxShadow: (t) => `0 12px 40px ${alpha(t.palette.secondary.main, 0.28)}`,
                            }}
                        >
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={2}
                                alignItems={{ xs: "stretch", sm: "center" }}
                                justifyContent="space-between"
                            >
                                <Stack direction="row" spacing={2} alignItems="flex-start">
                                    <Box
                                        sx={{
                                            width: 56,
                                            height: 56,
                                            borderRadius: 2,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            bgcolor: alpha("#fff", 0.15),
                                        }}
                                    >
                                        <MenuBookOutlinedIcon sx={{ fontSize: 32 }} />
                                    </Box>
                                    <Stack spacing={0.75}>
                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                            <Chip
                                                icon={<ClassOutlinedIcon sx={{ fontSize: "16px !important" }} />}
                                                label={className}
                                                size="small"
                                                sx={{
                                                    bgcolor: alpha("#fff", 0.18),
                                                    color: "common.white",
                                                    fontWeight: 700,
                                                }}
                                            />
                                        </Stack>
                                        <Typography variant="h4" component="h1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                                            Subjects
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.9, maxWidth: 560 }}>
                                            Compulsory vs elective, MIE syllabus codes, and{" "}
                                            <strong>periods per week</strong> for this class.
                                        </Typography>
                                    </Stack>
                                </Stack>
                                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="stretch">
                                    <Button
                                        variant="contained"
                                        size="large"
                                        startIcon={<PostAddIcon />}
                                        onClick={() =>
                                            navigate(`/Admin/addsubject/${classID}`, {
                                                state: { className },
                                            })
                                        }
                                        sx={{
                                            bgcolor: "common.white",
                                            color: "secondary.main",
                                            fontWeight: 600,
                                            px: 2.5,
                                            py: 1.25,
                                            borderRadius: 2,
                                            boxShadow: "none",
                                            "&:hover": {
                                                bgcolor: (t) => alpha(t.palette.common.white, 0.92),
                                                boxShadow: (t) => `0 4px 20px ${alpha(t.palette.common.black, 0.12)}`,
                                            },
                                        }}
                                    >
                                        Add subjects
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="large"
                                        color="inherit"
                                        startIcon={<DeleteOutlineIcon />}
                                        onClick={openDeleteAllSubjectsDialog}
                                        disabled={!hasRows}
                                        sx={{
                                            borderColor: alpha("#fff", 0.55),
                                            color: "common.white",
                                            borderRadius: 2,
                                            "&:hover": {
                                                borderColor: "common.white",
                                                bgcolor: alpha("#fff", 0.08),
                                            },
                                        }}
                                    >
                                        Delete all in class
                                    </Button>
                                </Stack>
                            </Stack>
                        </Paper>

                        {error && (
                            <Alert severity="error" variant="filled" sx={{ borderRadius: 2 }}>
                                {error?.message || "Could not load subjects."}
                            </Alert>
                        )}

                        {!error && (
                            <Paper
                                elevation={0}
                                sx={{
                                    borderRadius: 3,
                                    overflow: "hidden",
                                    border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                                    boxShadow: (t) => `0 4px 24px ${alpha(t.palette.common.black, 0.06)}`,
                                }}
                            >
                                {loading && (
                                    <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                                        <TableSkeleton />
                                    </Box>
                                )}

                                {showEmpty && (
                                    <Box sx={{ py: { xs: 6, sm: 8 }, px: 2, textAlign: "center" }}>
                                        <Typography variant="h6" gutterBottom fontWeight={600}>
                                            No subjects in {className} yet
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, mx: "auto", mb: 3 }}>
                                            {typeof response === "string"
                                                ? response
                                                : "Add subjects from the school list and set weekly periods."}
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            size="large"
                                            startIcon={<PostAddIcon />}
                                            onClick={() =>
                                            navigate(`/Admin/addsubject/${classID}`, {
                                                state: { className },
                                            })
                                        }
                                            sx={{ borderRadius: 2, px: 3, fontWeight: 600 }}
                                        >
                                            Add subjects
                                        </Button>
                                    </Box>
                                )}

                                {showTable && (
                                    <>
                                        <Box
                                            sx={{
                                                px: { xs: 2, sm: 2.5 },
                                                py: 2,
                                                display: "flex",
                                                flexWrap: "wrap",
                                                gap: 1,
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                borderBottom: (t) => `1px solid ${t.palette.divider}`,
                                                bgcolor: (t) => alpha(t.palette.secondary.main, 0.06),
                                            }}
                                        >
                                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                                <Typography variant="subtitle1" fontWeight={600}>
                                                    {className} subjects
                                                </Typography>
                                                <Chip
                                                    size="small"
                                                    label={`${rows.length} total`}
                                                    color="secondary"
                                                    variant="outlined"
                                                    sx={{ fontWeight: 600, borderRadius: 2 }}
                                                />
                                            </Stack>
                                            <Typography variant="body2" color="text.secondary">
                                                {paginationRangeLabel(page, pageSize, totalItems, "subjects")}
                                            </Typography>
                                        </Box>
                                        <TableContainer>
                                            <Table size="medium" aria-label="subjects table">
                                                <TableHead>
                                                    <TableRow>
                                                        {headCells.map((col) => (
                                                            <TableCell
                                                                key={col.id}
                                                                align={col.align || "left"}
                                                                sx={{
                                                                    fontWeight: 700,
                                                                    fontSize: 12,
                                                                    letterSpacing: 0.6,
                                                                    textTransform: "uppercase",
                                                                    color: "text.secondary",
                                                                    bgcolor: (t) => alpha(t.palette.secondary.main, 0.06),
                                                                    borderBottom: (t) => `1px solid ${t.palette.divider}`,
                                                                    py: 2,
                                                                    width: col.width,
                                                                }}
                                                            >
                                                                {col.label}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {paginatedItems.map((row) => (
                                                        <TableRow
                                                            key={row.id}
                                                            hover
                                                            sx={{
                                                                "&:hover": {
                                                                    boxShadow: (t) => `inset 3px 0 0 ${t.palette.secondary.main}`,
                                                                },
                                                            }}
                                                        >
                                                            <TableCell sx={{ py: 2 }}>
                                                                <Typography fontWeight={600}>{row.subName}</Typography>
                                                            </TableCell>
                                                            <TableCell sx={{ py: 2 }}>
                                                                {row.teacherLabel === "Unassigned" ? (
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        Unassigned
                                                                    </Typography>
                                                                ) : (
                                                                    <Chip
                                                                        size="small"
                                                                        label={row.teacherLabel}
                                                                        color="primary"
                                                                        variant="outlined"
                                                                        sx={{ fontWeight: 600, borderRadius: 2 }}
                                                                    />
                                                                )}
                                                            </TableCell>
                                                            <TableCell sx={{ py: 2 }}>
                                                                <Chip
                                                                    size="small"
                                                                    label={row.isElective ? "Elective" : "Compulsory"}
                                                                    color={row.isElective ? "info" : "success"}
                                                                    variant="outlined"
                                                                    sx={{ fontWeight: 600, borderRadius: 2 }}
                                                                />
                                                            </TableCell>
                                                            <TableCell sx={{ py: 2 }}>
                                                                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                                                                    {row.mieCode}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell sx={{ py: 2 }}>
                                                                <Chip label={String(row.sessions)} size="small" sx={{ fontWeight: 700 }} />
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ py: 2 }}>
                                                                <RowActions
                                                                    row={row}
                                                                    classId={classID}
                                                                    onDeleteSubject={openDeleteSubjectDialog}
                                                                />
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                        {pageCount > 1 && (
                                            <Pagination
                                                count={pageCount}
                                                page={page}
                                                onChange={(_, v) => setPage(v)}
                                                color="primary"
                                                sx={paginationSx}
                                            />
                                        )}
                                    </>
                                )}
                            </Paper>
                        )}
                    </Stack>
                </Fade>
            </Container>
            <Popup message={message} setShowPopup={setShowPopup} showPopup={showPopup} />
            <ConfirmDeleteDialog
                open={Boolean(deleteDialog)}
                onClose={closeDeleteDialog}
                onConfirm={handleConfirmDelete}
                title={deleteDialog?.title ?? ""}
                description={deleteDialog?.description ?? ""}
                confirmWord={deleteDialog?.confirmWord ?? ""}
            />
        </Box>
    );
};

export default ShowSubjects;
